/**
 * Minimal, safe Markdown renderer for assistant chat messages.
 *
 * Rendered entirely as React elements (never `dangerouslySetInnerHTML`), so model
 * output cannot inject HTML/scripts. Supports the subset the assistant actually
 * emits: headings, paragraphs, fenced code, inline code, bold/italic/strike,
 * http(s) links, blockquotes, ordered/unordered lists and GFM tables.
 *
 * Streaming-friendly: incomplete markup (unclosed `**`, ``` etc.) is rendered as
 * literal text instead of throwing.
 */
import type { ReactNode } from 'react';

interface MarkdownProps {
  text: string;
  className?: string;
}

const INLINE_PATTERN =
  /(`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]+\]\((https?:\/\/[^\s)]+)\))/gu;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  INLINE_PATTERN.lastIndex = 0;
  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    const token = match[0];
    const start = match.index;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const key = `${keyPrefix}-${nodes.length}`;
    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-black/40 px-1 py-0.5 font-mono text-[0.85em] text-indigo-200"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push(
        <strong key={key} className="font-semibold text-white">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith('~~')) {
      nodes.push(
        <del key={key} className="text-slate-400">
          {token.slice(2, -2)}
        </del>,
      );
    } else if (token.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/u.exec(token);
      if (linkMatch) {
        nodes.push(
          <a
            key={key}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer noopener"
            className="text-indigo-300 underline decoration-indigo-400/40"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    lastIndex = start + token.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function isTableSeparator(line: string): boolean {
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/u.test(cell));
}

function splitTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/u, '').replace(/\|$/u, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

function isBlockStart(line: string): boolean {
  return (
    /^\s*```/u.test(line) ||
    /^\s*#{1,6}\s+/u.test(line) ||
    /^\s*>\s?/u.test(line) ||
    /^\s*([-*+]|\d+\.)\s+/u.test(line) ||
    /^\s*---+\s*$/u.test(line) ||
    (line.includes('|') && isTableSeparator(line))
  );
}

function renderTable(header: string[], rows: string[][], key: string): ReactNode {
  return (
    <div key={key} className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-left text-[0.85em]">
        <thead>
          <tr>
            {header.map((cell, index) => (
              <th
                key={index}
                className="border border-white/10 bg-white/5 px-2 py-1 font-semibold text-slate-200"
              >
                {renderInline(cell, `${key}-h${index}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="border border-white/10 px-2 py-1 align-top text-slate-300"
                >
                  {renderInline(cell, `${key}-r${rowIndex}c${cellIndex}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function parseBlocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n?/gu, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;
  let keyCounter = 0;
  const nextKey = (): string => `md-${keyCounter++}`;

  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (line.trim().length === 0) {
      index += 1;
      continue;
    }

    const fence = /^\s*```(.*)$/u.exec(line);
    if (fence) {
      const language = (fence[1] ?? '').trim();
      index += 1;
      const code: string[] = [];
      while (index < lines.length && !/^\s*```\s*$/u.test(lines[index] ?? '')) {
        code.push(lines[index] ?? '');
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(
        <pre
          key={nextKey()}
          className="my-2 overflow-x-auto rounded-lg bg-black/40 p-2.5 text-[0.8em] leading-relaxed text-slate-200"
        >
          <code data-language={language || undefined}>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/u.exec(line);
    if (heading) {
      const level = Math.min((heading[1] ?? '#').length, 6);
      const sizes = ['text-lg', 'text-base', 'text-[0.95rem]', 'text-sm', 'text-sm', 'text-sm'];
      blocks.push(
        <p key={nextKey()} className={`mt-2 mb-1 font-semibold text-white ${sizes[level - 1]}`}>
          {renderInline(heading[2] ?? '', `h-${keyCounter}`)}
        </p>,
      );
      index += 1;
      continue;
    }

    if (/^\s*---+\s*$/u.test(line)) {
      blocks.push(<hr key={nextKey()} className="my-3 border-white/10" />);
      index += 1;
      continue;
    }

    if (/^\s*>\s?/u.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^\s*>\s?/u.test(lines[index] ?? '')) {
        quote.push((lines[index] ?? '').replace(/^\s*>\s?/u, ''));
        index += 1;
      }
      blocks.push(
        <blockquote
          key={nextKey()}
          className="my-2 border-l-2 border-indigo-400/40 pl-2.5 text-slate-400"
        >
          {quote.map((item, quoteIndex) => (
            <p key={quoteIndex} className="whitespace-pre-wrap break-words">
              {renderInline(item, `q-${keyCounter}-${quoteIndex}`)}
            </p>
          ))}
        </blockquote>,
      );
      continue;
    }

    const unordered = /^\s*([-*+])\s+(.*)$/u.exec(line);
    const ordered = /^\s*(\d+)\.\s+(.*)$/u.exec(line);
    if (unordered || ordered) {
      const isOrdered = ordered !== null;
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index] ?? '';
        const match = isOrdered
          ? /^\s*\d+\.\s+(.*)$/u.exec(current)
          : /^\s*[-*+]\s+(.*)$/u.exec(current);
        if (!match) break;
        items.push(match[1] ?? '');
        index += 1;
      }
      const ListTag = isOrdered ? 'ol' : 'ul';
      blocks.push(
        <ListTag
          key={nextKey()}
          className={`my-1.5 space-y-0.5 pl-5 ${isOrdered ? 'list-decimal' : 'list-disc'}`}
        >
          {items.map((item, itemIndex) => (
            <li key={itemIndex} className="whitespace-pre-wrap break-words">
              {renderInline(item, `li-${keyCounter}-${itemIndex}`)}
            </li>
          ))}
        </ListTag>,
      );
      continue;
    }

    if (line.includes('|') && isTableSeparator(lines[index + 1] ?? '')) {
      const header = splitTableRow(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && (lines[index] ?? '').includes('|')) {
        const row = splitTableRow(lines[index] ?? '');
        if (row.every((cell) => cell.length === 0)) break;
        rows.push(row);
        index += 1;
      }
      blocks.push(renderTable(header, rows, nextKey()));
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? '';
      if (current.trim().length === 0) break;
      if (paragraph.length > 0 && isBlockStart(current)) break;
      paragraph.push(current);
      index += 1;
    }
    blocks.push(
      <p key={nextKey()} className="whitespace-pre-wrap break-words">
        {renderInline(paragraph.join('\n'), `p-${keyCounter}`)}
      </p>,
    );
  }
  return blocks;
}

export function Markdown({ text, className }: MarkdownProps) {
  return <div className={className}>{parseBlocks(text)}</div>;
}
